import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";
import { GoldBadge, SectionHead, Reveal } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMOUNTS = [
  { key: "donate_25", label: "$25", blurb: "Fuels a patrol vehicle for a night on the asphalt", testId: "donate-25-button" },
  { key: "donate_50", label: "$50", blurb: "Covers court-support materials for one survivor", testId: "donate-50-button" },
  { key: "donate_100", label: "$100", blurb: "Funds a Broken Crayons youth support session", testId: "donate-100-button" },
  { key: "donate_250", label: "$250", blurb: "Sponsors a full week of community safety patrols", testId: "donate-250-button" },
];

export default function DonateSection() {
  const [busy, setBusy] = useState(null);

  const donate = async (key) => {
    setBusy(key);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: key,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      setBusy(null);
      toast.error("Could not open the secure donation page. Please try again.");
    }
  };

  return (
    <section data-testid="donate-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
      <Reveal>
        <div className="text-center max-w-3xl mx-auto">
          <GoldBadge testId="donate-badge">Fuel the Frontline</GoldBadge>
          <h2 className="font-display mt-5 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50">
            Every dollar keeps a patrol on the street
          </h2>
          <div className="h-[2px] w-16 bg-gradient-to-r from-amber-400 via-amber-600 to-transparent my-4 mx-auto" />
          <p className="text-base sm:text-lg leading-relaxed text-slate-300">
            P.A.I.N. runs on community support. Your donation goes directly to community safety
            patrols, court support for survivors, and youth programs — not overheads.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {AMOUNTS.map((a, i) => (
          <Reveal key={a.key} delay={i * 0.08}>
            <button
              data-testid={a.testId}
              onClick={() => donate(a.key)}
              disabled={busy !== null}
              className="card-pain w-full h-full p-6 text-left group disabled:opacity-60"
            >
              <Heart className="h-6 w-6 text-amber-400 group-hover:scale-110 transition-transform" />
              <p className="font-display mt-4 text-3xl font-bold gold-text-gradient">{a.label}</p>
              <p className="mt-2 text-sm text-slate-300 leading-snug">{a.blurb}</p>
              <p className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-400">
                {busy === a.key ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening secure checkout...
                  </>
                ) : (
                  "Donate securely"
                )}
              </p>
            </button>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-slate-500">
        Secure card payment via Stripe. All amounts in AUD. P.A.I.N. is a registered Australian charity.
      </p>
    </section>
  );
}
